/**
 * @fileoverview Bringing in a Google Photos pick that outlived its dialog or its tab.
 *
 * On a phone, Grow's tab is put away while the gardener chooses in Google
 * Photos, and by the time they tap Done the dialog may be closed, or the tab
 * frozen, thrown away and reloaded, or Grow opened afresh by Back to Grow.
 * The pick is kept in localStorage until its photos are in
 * (GooglePhotosService, googlePhotosFlow's PendingPick, with its target); this
 * asks the service to take it up again when the app opens, whenever it comes
 * back to the front, and when the network returns.
 *
 * Photos whose dialog is still open go to that dialog, as always. The rest
 * come here (the service's fallback) and go where the pick was going (B40,
 * googlePhotosTarget): onto the plant, onto the bed, into the packet scan
 * (held for it, shared/lib/lateHandoff, and the bed opened), to Today's
 * picture (held, the profile opened, the dialog previews it and Save saves
 * it), over the photo being replaced, to the profile photo. Only a pick started on the Photos page, or
 * with no target, goes to the tray ("Photos to sort", on the target's bed when
 * it had one), and only that is AI-guessed. A toast says where, "Adding N
 * photos from Google Photos…". A failure is said with the standard sentence
 * (D31, lib/serverFailure): reported, no "try again", the support address.
 * The service has already sent it to Sentry with its reason and step.
 * Each photo goes with Google's item id, so the same item picked twice is skipped by the server (B61); the skipped
 * ones are said once, "Already added, N copies skipped", with Undo (lib/photoCopies; words photoCopies.*).
 *
 * Where: Layout, once, for a signed-in gardener (the photos are fetched and
 * stored through our server, which needs the sign-in).
 * API: POST /photos/add or POST /locations/:id/photos (uploadPhotoTo), PUT
 * the photo's file (replacePhotoFileFor), POST the profile photo (uploadProfilePhoto), or
 * POST /tray (addToTray, multipart: the photo, taken_at, locationId), one
 * photo at a time; the fetching itself is the service's
 * (POST /google-photos/media). Each photo that lands on the tray has the tray
 * read again (GET /tray, trayPhotoLanded), so its pile is the server's; the
 * plants and the beds are read again after theirs (refreshPlantsAndBeds), and
 * the season brief is marked stale (seasonWritten: the diary lists the new
 * photo; W24 Job H), and nothing else.
 * Words (en/es): plants.photo.source.resumeAddingOne, resumeAdding,
 * resumeToTray, resumeToPlant, resumeToBed, resumeToPacket, resumePacketKept,
 * resumeToToday, resumeToProfile, resumeToReplace; tray.uploadFailed; serverFailure.message.
 *
 * @module GooglePhotosResume
 */

import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/shared/components/ui/use-toast';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { GooglePhotosService, type GooglePhotoPick, type PickTarget } from '@/shared/services/GooglePhotosService';
import { lateDelivery, photosForRoute, refreshesGardenLists, type LateRoute } from '@/shared/services/googlePhotosTarget';
import { preparePhoto } from '@/shared/lib/preparePhoto';
import { addToTray, type TrayPhoto } from '@/shared/api/tray';
import { replacePhotoFileFor, uploadPhotoTo } from '@/shared/api/photos';
import { PhotoCopySkipped } from '@/shared/lib/photoCopiesCore';
import { sayCopiesSkipped } from '@/shared/lib/photoCopies';
import { plantSubject, type Subject } from '@/shared/api/subject';
import { uploadProfilePhoto } from '@/shared/api/users';
import { useAuth } from '@/shared/hooks/useAuth';
import { DateTime, getCurrentDate } from '@/shared/utils/dateUtils';
import { holdLate, hasLate } from '@/shared/lib/lateHandoff';
import { refreshPlantsAndBeds } from '@/features/packets/usePacketsFiled';
import { seasonWritten } from '@/shared/lib/briefFreshness';
import { debugLog } from '@/config/config';
import { serverFailureText, serverFailureWords } from '@/shared/lib/serverFailure';
import { TRAY_QUERY_KEY, trayPhotoLanded } from '../lib/useTray';

/** The word for where each route's photos are going, under the "Adding N photos" toast. */
const ROUTE_WORDS: Record<LateRoute, string> = {
  plant: 'plants.photo.source.resumeToPlant',
  bed: 'plants.photo.source.resumeToBed',
  packet: 'plants.photo.source.resumeToPacket',
  today: 'plants.photo.source.resumeToToday',
  profile: 'plants.photo.source.resumeToProfile',
  replace: 'plants.photo.source.resumeToReplace',
  tray: 'plants.photo.source.resumeToTray',
};

/**
 * Resumes pending Google Photos picks and delivers photos with no dialog left
 * to where the pick was going. Draws nothing of its own; it speaks by toast.
 *
 * @returns Nothing to draw.
 */
export function GooglePhotosResume(): null {
  const { reactTranslate: t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { mysqlUser, updateMysqlUser } = useAuth();
  /** The "Adding N photos" toast while it shows, to take down when they are in. */
  const adding = React.useRef<{ dismiss: () => void } | null>(null);
  /** The words as last rendered, read by the service's calls without re-registering them. */
  const words = React.useRef(t);
  words.current = t;
  /** The signed-in gardener and the way to show their new photo, read the same way. */
  const account = React.useRef({ mysqlUser, updateMysqlUser });
  account.current = { mysqlUser, updateMysqlUser };
  /** The router's navigate, read the same way. */
  const goTo = React.useRef(navigate);
  goTo.current = navigate;

  React.useEffect(() => {
    const service = GooglePhotosService.getInstance();

    /** Takes the "Adding" toast down. */
    const doneAdding = (): void => {
      adding.current?.dismiss();
      adding.current = null;
    };

    /**
     * Shrinks one photo and gives it its day: Google's, else today.
     *
     * @param pick - The photo and Google's date.
     * @returns The photo ready to send and its day.
     * @throws When the picture could not be made ready.
     */
    const ready = async ({ file, takenAt }: GooglePhotoPick): Promise<{ file: File; day: Date }> => {
      const prepared = await preparePhoto(file);
      // A picture that could not be made ready is not sent (the one rule, preparePhoto); counted as failed.
      if (!prepared.ok) throw new Error(prepared.message);
      return { file: prepared.file, day: takenAt ?? getCurrentDate().startOf('day').toJSDate() };
    };

    /**
     * Puts one photo on the tray: shrunk, dated by Google (its copies carry
     * no EXIF), on the target's bed.
     *
     * @param pick - The photo and Google's date.
     * @param locationId - The bed, or null.
     * @returns The photo as the tray has it.
     */
    const toTray = async ({ file, takenAt, googleItemId }: GooglePhotoPick, locationId: number | null): Promise<TrayPhoto> => {
      const prepared = await preparePhoto(file);
      if (!prepared.ok) throw new Error(prepared.message);
      const when = takenAt ? DateTime.fromJSDate(takenAt) : null;
      const photo = await addToTray(prepared.file, when, locationId, { googleItemId });
      trayPhotoLanded(queryClient, photo);
      return photo;
    };

    /**
     * Puts one photo on a plant or a bed, as that screen's own upload does:
     * no tray, no AI guess.
     *
     * @param pick - The photo.
     * @param subject - The plant or the bed.
     */
    const toSubject = async (pick: GooglePhotoPick, subject: Subject): Promise<void> => {
      const { file, day } = await ready(pick);
      await uploadPhotoTo(subject, file, day, null, undefined, { googleItemId: pick.googleItemId });
    };

    /**
     * Hands the photo to Today's picture dialog and opens it on the profile,
     * to be previewed and saved with Save, as a chosen one is.
     *
     * @param pick - The photo.
     */
    const toToday = async (pick: GooglePhotoPick): Promise<void> => {
      const { file } = await ready(pick);
      holdLate('today', file, null, Date.now());
      goTo.current('/profile?s=prefs');
    };

    /**
     * Swaps the photo's picture for the new one, keeping the photo (its date,
     * its note, its place).
     *
     * @param pick - The photo.
     * @param subject - The plant or bed the photo is on.
     * @param photoId - The photo being replaced.
     */
    const toReplace = async (pick: GooglePhotoPick, subject: Subject, photoId: number): Promise<void> => {
      const { file } = await ready(pick);
      await replacePhotoFileFor(subject, photoId, file);
    };

    /**
     * Makes the photo the profile picture, shown at once.
     *
     * @param pick - The photo.
     */
    const toProfile = async (pick: GooglePhotoPick): Promise<void> => {
      const { mysqlUser: me, updateMysqlUser: show } = account.current;
      if (!me) throw new Error('Signed out before the profile photo arrived');
      const { file } = await ready(pick);
      const formData = new FormData();
      formData.append('photo', file);
      const result = await uploadProfilePhoto(me.user_id, formData);
      if (!result.success) throw new Error('The profile photo was not saved');
      // The address does not change with the picture; the stamp forces a fresh fetch.
      show({ ...result.user, profile_photo_url: `${result.user.profile_photo_url}?t=${Date.now()}` });
    };

    /**
     * Hands the label photo to the packet scan and opens the bed it was for;
     * with no bed, says where to read it.
     *
     * @param pick - The label photo.
     * @param locationId - The bed, or null.
     */
    const toPacket = async (pick: GooglePhotoPick, locationId: number | null): Promise<void> => {
      const { file } = await ready(pick);
      holdLate('packet', file, locationId, Date.now());
      if (locationId !== null) goTo.current(`/beds/${locationId}`);
      else if (hasLate('packet')) toast({ title: words.current('plants.photo.source.resumeToPacket'), description: words.current('plants.photo.source.resumePacketKept') });
    };

    service.setFallback({
      adding: (count, target) => {
        doneAdding();
        const { route } = lateDelivery(target);
        const shown = photosForRoute(route, new Array<number>(count).fill(0)).length;
        adding.current = toast({
          title: shown === 1
            ? words.current('plants.photo.source.resumeAddingOne')
            : words.current('plants.photo.source.resumeAdding', { count: shown }),
          description: words.current(ROUTE_WORDS[route]),
        });
      },
      deliver: async (picks: GooglePhotoPick[], target: PickTarget) => {
        const { route, id, plantId, locationId } = lateDelivery(target);
        let failed = 0;
        let ours: string | null = null;
        /** The exact copies the server skipped (B61): said once, with Undo, when the pick is in. */
        const skipped: PhotoCopySkipped[] = [];
        for (const pick of photosForRoute(route, picks)) {
          try {
            if (route === 'plant' && id !== null) await toSubject(pick, plantSubject(id));
            else if (route === 'bed' && id !== null) await toSubject(pick, { kind: 'location', id });
            else if (route === 'replace' && id !== null) await toReplace(pick, plantId !== null ? plantSubject(plantId) : { kind: 'location', id: locationId ?? 0 }, id);
            else if (route === 'today') await toToday(pick);
            else if (route === 'profile') await toProfile(pick);
            else if (route === 'packet') await toPacket(pick, locationId);
            else await toTray(pick, locationId);
          } catch (error) {
            if (error instanceof PhotoCopySkipped) {
              skipped.push(error);
              continue;
            }
            debugLog('useGooglePhotos', 'Delivering a Google photo failed', error);
            failed += 1;
            ours = ours ?? serverFailureText(error, 'Google Photos late pick');
          }
        }
        doneAdding();
        if (route === 'tray') {
          await queryClient.invalidateQueries({ queryKey: TRAY_QUERY_KEY });
        } else if (refreshesGardenLists(route)) {
          // Only what a photo on a plant or a bed changes: the plants, the beds and the season's diary.
          refreshPlantsAndBeds(queryClient);
          seasonWritten(queryClient);
        }
        sayCopiesSkipped(skipped, () => {
          if (route === 'tray') void queryClient.invalidateQueries({ queryKey: TRAY_QUERY_KEY });
          else if (refreshesGardenLists(route)) refreshPlantsAndBeds(queryClient);
        });
        if (failed > 0) {
          toast({
            title: words.current('tray.uploadFailed', { count: failed }),
            description: ours ?? undefined,
            variant: 'destructive',
          });
        }
      },
      failed: (error) => {
        doneAdding();
        toast({ title: serverFailureWords(error), variant: 'destructive' });
      },
    });

    /** Takes up a pending pick, if any. */
    const resume = (): void => service.resume();
    /** Back in front: the moment a phone gives us. */
    const onVisible = (): void => { if (document.visibilityState === 'visible') resume(); };

    resume();
    document.addEventListener('visibilitychange', onVisible);
    // A page restored from the back-forward cache runs no effects again.
    window.addEventListener('pageshow', resume);
    window.addEventListener('online', resume);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', resume);
      window.removeEventListener('online', resume);
      service.setFallback(null);
    };
  }, [queryClient]);

  return null;
}
